/**
 * Generated by orval 🍺
 * Do not edit manually.
 * CommunityCliQ API
 *  ## Authentication
 *
 *   Every request must include an API access token generated in the CommunityCliQ \
 *   application:
 *
 *   ```
 *   Authorization: X-API-TOKEN
 *   ```
 *
 * OpenAPI spec version: V2.X
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  CommandErrorResponse,
  OnboardingProfileDetails,
  OnboardingRequest,
  UserOnboardingStatusResponse,
} from "../../model";

import { customInstance } from "../../../lib/SpringAxios";
import type { ErrorType } from "../../../lib/SpringAxios";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") {
      continue;
    }
    Object.defineProperty(result, key, {
      configurable: true,
      enumerable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Api request to get a persons information when they are onboarding.
 * @summary Api request to get a persons information when they are onboarding.
 */
export const getOnboardingProfile = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<OnboardingProfileDetails>(
    { method: "GET", signal, url: `/api/v2/profile/onboarding` },
    options,
  );
};

export const getGetOnboardingProfileQueryKey = () => {
  return [`/api/v2/profile/onboarding`] as const;
};

export const getGetOnboardingProfileQueryOptions = <
  TData = Awaited<ReturnType<typeof getOnboardingProfile>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getOnboardingProfile>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOnboardingProfileQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOnboardingProfile>>
  > = ({ signal }) => getOnboardingProfile(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOnboardingProfile>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetOnboardingProfileQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOnboardingProfile>>
>;
export type GetOnboardingProfileQueryError = ErrorType<CommandErrorResponse>;

export function useGetOnboardingProfile<
  TData = Awaited<ReturnType<typeof getOnboardingProfile>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOnboardingProfile>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOnboardingProfile>>,
          TError,
          Awaited<ReturnType<typeof getOnboardingProfile>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOnboardingProfile<
  TData = Awaited<ReturnType<typeof getOnboardingProfile>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOnboardingProfile>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOnboardingProfile>>,
          TError,
          Awaited<ReturnType<typeof getOnboardingProfile>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOnboardingProfile<
  TData = Awaited<ReturnType<typeof getOnboardingProfile>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOnboardingProfile>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Api request to get a persons information when they are onboarding.
 */

export function useGetOnboardingProfile<
  TData = Awaited<ReturnType<typeof getOnboardingProfile>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOnboardingProfile>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetOnboardingProfileQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Api request to update a persons information when they are onboarding.
 * @summary Api request to update a persons information when they are onboarding.
 */
export const updateOnboardingProfileSettings = (
  onboardingRequest: OnboardingRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    {
      data: onboardingRequest,
      headers: { "Content-Type": "application/json" },
      method: "PUT",
      signal,
      url: `/api/v2/profile/onboarding`,
    },
    options,
  );
};

export const getUpdateOnboardingProfileSettingsMutationKey = () =>
  ["updateOnboardingProfileSettings"] as const;

export const getUpdateOnboardingProfileSettingsMutationOptions = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOnboardingProfileSettings>>,
    TError,
    UpdateOnboardingProfileSettingsMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOnboardingProfileSettings>>,
  TError,
  UpdateOnboardingProfileSettingsMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateOnboardingProfileSettingsMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOnboardingProfileSettings>>,
    UpdateOnboardingProfileSettingsMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return updateOnboardingProfileSettings(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOnboardingProfileSettingsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateOnboardingProfileSettings>>
>;
export type UpdateOnboardingProfileSettingsMutationBody = OnboardingRequest;
export type UpdateOnboardingProfileSettingsMutationError =
  ErrorType<CommandErrorResponse>;
export type UpdateOnboardingProfileSettingsMutationVariables = {
  data: OnboardingRequest;
};

/**
 * @summary Api request to update a persons information when they are onboarding.
 */
export const useUpdateOnboardingProfileSettings = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateOnboardingProfileSettings>>,
      TError,
      UpdateOnboardingProfileSettingsMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateOnboardingProfileSettings>>,
  TError,
  UpdateOnboardingProfileSettingsMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateOnboardingProfileSettingsMutationOptions(options),
    queryClient,
  );
};
/**
 * Api request to get if a person has completed onboarding or not.
 * @summary Api request to get if a person has completed onboarding or not.
 */
export const getOnboardingStatus = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<UserOnboardingStatusResponse>(
    { method: "GET", signal, url: `/api/v2/profile/onboarding/status` },
    options,
  );
};

export const getGetOnboardingStatusQueryKey = () => {
  return [`/api/v2/profile/onboarding/status`] as const;
};

export const getGetOnboardingStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getOnboardingStatus>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getOnboardingStatus>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOnboardingStatusQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOnboardingStatus>>
  > = ({ signal }) => getOnboardingStatus(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOnboardingStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetOnboardingStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOnboardingStatus>>
>;
export type GetOnboardingStatusQueryError = ErrorType<CommandErrorResponse>;

export function useGetOnboardingStatus<
  TData = Awaited<ReturnType<typeof getOnboardingStatus>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOnboardingStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOnboardingStatus>>,
          TError,
          Awaited<ReturnType<typeof getOnboardingStatus>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOnboardingStatus<
  TData = Awaited<ReturnType<typeof getOnboardingStatus>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOnboardingStatus>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOnboardingStatus>>,
          TError,
          Awaited<ReturnType<typeof getOnboardingStatus>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOnboardingStatus<
  TData = Awaited<ReturnType<typeof getOnboardingStatus>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOnboardingStatus>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Api request to get if a person has completed onboarding or not.
 */

export function useGetOnboardingStatus<
  TData = Awaited<ReturnType<typeof getOnboardingStatus>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOnboardingStatus>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetOnboardingStatusQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
